<script setup>
import { computed, onMounted, watch, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useCustomerStore } from '@/stores/customer.js'
import {useToast} from "primevue";
import Card from 'primevue/card';
import SpeedDial from 'primevue/speeddial';
import CustomerForm from "./CustomerForm.vue";
import ConfirmDeleteDialog from "../common/ConfirmDeleteDialog.vue";
import Contacts from "@/components/Contacts.vue";
import { useResponsiblesStore } from '@/stores/responsibles.js'
import ResponsibilityList from '@/components/common/ResponsibilityList.vue'
import CreateReminderDialog from "@/components/common/CreateReminderDialog.vue";
import EntityMessageDialog from "@/components/messages/EntityMessageDialog.vue";
import TaskCreateDialog from "@/components/tasks/TaskCreateDialog.vue";
import ConfirmArchiveDialog from "@/components/common/ConfirmArchiveDialog.vue";
import EventHistoryDialog from "@/components/common/EventHistoryDialog.vue";
import { usePermissions } from '@/composables/usePermissions.js';

const route = useRoute()
const router = useRouter()
const customerStore = useCustomerStore()
const responsiblesStore = useResponsiblesStore()
const { hasPermission } = usePermissions()


const customerId = computed(() => {
  const raw = route.params.id
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
})

watch(customerId, (id) => {
  if (id) responsiblesStore.loadCurrent('customer', id)
}, { immediate: true })

const showEditDialog = ref(false)
const showConfirm = ref(false)
const showConfirmArchive = ref(false)
const showConfirmUnarchive = ref(false)
const showMessageDialog = ref(false)
const showCreateTask = ref(false)
const showEventHistory = ref(false)



const toast = useToast();

onMounted(() => {
  customerStore.loadCustomer(route.params.id)
})
watch(() => route.params.id, (id) => customerStore.loadCustomer(id))

const customer = computed(() => customerStore.customer)
const customerResponsiblesRaw = computed(() => responsiblesStore.items('customer', customerId.value))
const isArchived = computed(() => customer.value?.status === 'ARCHIVED')

const responsible = computed(() => {
  const list = customerResponsiblesRaw.value || []
  if (!Array.isArray(list) || list.length === 0) {
    return { id: null, name: '' }
  }
  const item = list.find(r => r?.is_primary) ?? list[0]
  const u = item?.user
  const id = Number(u?.id)
  const name = u?.full_name
      || [u?.last_name, u?.first_name].filter(Boolean).join(' ')
      || u?.name
      || ''
  return { id: Number.isFinite(id) && id > 0 ? id : null, name }
})


function openResponsibleMessage() {
  if (!responsible.value?.id) {
    toast.add({ severity: 'warn', summary: 'Не указан ответственный', life: 2000 })
    return
  }
  showMessageDialog.value = true
}

function handleEditSave(updatedData) {
  customerStore.updateCustomer(customer.value.id, updatedData, 'put')
      .then(() => {
        toast.add({
          severity: 'success',
          summary: 'Клиент обновлен',
          detail: 'Данные клиента успешно обновлены',
          life: 2500,
        })
      })
      .catch(() => {
        toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Не удалось обновить данные клиента',
          life: 4000,
        })
      })
  showEditDialog.value = false
}


async function onArchiveConfirm() {
  try {
    await customerStore.archiveCustomer(route.params.id)
    toast.add({
      severity: 'success',
      summary: 'Клиент архивирован',
      detail: 'Клиент перемещен в архив',
      life: 2500,
    })
    await router.push('/customers')
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось архивировать клиента',
      life: 4000,
    })
  }
}

async function onUnarchiveConfirm() {
  try {
    await customerStore.unarchiveCustomer(route.params.id)
    toast.add({
      severity: 'success',
      summary: 'Клиент возвращён из архива',
      detail: 'Статус клиента изменён на ACTIVE',
      life: 2500,
    })
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось вернуть клиента из архива',
      life: 4000,
    })
  }
}

const blockerLabels = {
  works: 'Работы',
  contracts: 'Договоры',
  contacts: 'Контакты',
  comments: 'Комментарии'
}

function formatBlockers(blockers) {
  if (!blockers || typeof blockers !== 'object') return ''
  return Object.entries(blockers)
      .map(([key, count]) => `${blockerLabels[key] || key} (${count})`)
      .join('\n')
}

async function onDeleteConfirm() {
  try {
    await customerStore.deleteCustomer(route.params.id)
    toast.add({
      severity: 'success',
      summary: 'Клиент удален',
      detail: 'Данные клиента успешно удалены',
      life: 2500,
    });
    await router.push('/customers')
  } catch (e) {
    if (e.response?.status === 409 && e.response?.data?.blockers) {
      const data = e.response.data
      const blockersText = formatBlockers(data.blockers)
      const detail = `Найдены связанные сущности.\n${blockersText}\n Используйте архивирование.`
      toast.add({
        severity: 'warn',
        summary: 'Удаление невозможно',
        detail,
        life: 6000,
      });
    } else {
      toast.add({
        severity: 'error',
        summary: 'Ошибка',
        detail: 'Не удалось удалить клиента',
        life: 4000,
      });
    }
  }
}

const showCreateReminder = ref(false)

const reminderTarget = computed(() => {
  if (!customerId.value) return null
  return { app: 'customer', model: 'customer', objectId: Number(customerId.value) }
})
const taskContext = computed(() => {
  if (!customerId.value) return null
  return { app: 'customer', model: 'customer', objectId: Number(customerId.value) }
})

const menuItems = computed(() => {
  const items = []

  // Удалить - требует customer.delete_customer
  if (hasPermission('customer.delete_customer')) {
    items.push({
      label: 'Удалить',
      icon: 'pi pi-trash',
      severity: 'danger',
      command: () => {
        showConfirm.value = true
      }
    })
  }

  // Архивировать/Вернуть из архива - требует customer.archive_customer
  if (hasPermission('customer.archive_customer')) {
    items.push({
      label: isArchived.value ? 'Вернуть из архива' : 'Архивировать',
      icon: isArchived.value ? 'pi pi-sun' : 'pi pi-moon',
      severity: isArchived.value ? 'success' : 'danger',
      command: () => {
        const currentStatus = isArchived.value
        if (currentStatus) {
          showConfirmUnarchive.value = true
        } else {
          showConfirmArchive.value = true
        }
      }
    })
  }

  // Написать ответственному - доступно всем
  items.push({
    label: 'Написать ответственному',
    icon: 'pi pi-envelope',
    command: openResponsibleMessage
  })

  // Создать напоминание - доступно всем
  items.push({
    label: 'Создать напоминание',
    icon: 'pi pi-bell',
    command: () => {
      showCreateReminder.value = true
    }
  })

  // Создать задачу - требует task.create_for_customer
  if (hasPermission('task.create_for_customer')) {
    items.push({
      label: 'Создать задачу',
      icon: 'pi pi-pen-to-square',
      command: () => { showCreateTask.value = true }
    })
  }

  // История - доступна всем
  items.push({
    label: 'История',
    icon: 'pi pi-history',
    command: () => {
      showEventHistory.value = true
    }
  })

  // Редактировать - требует customer.change_customer
  if (hasPermission('customer.change_customer')) {
    items.push({
      label: 'Редактировать',
      icon: 'pi pi-pencil',
      command: () => {
        showEditDialog.value = true
      }
    })
  }

  return items
});
</script>

<template>
  <div>
    <div class="flex flex-col">
      <Card>
        <template #title>
          <div class="flex justify-between gap-2 items-center relative">
            <span>{{ customer?.short_name || '...' }}</span>
            <div style="position: relative; width: 44px; height: 44px;">
              <SpeedDial
                  :model="menuItems"
                  :radius="130"
                  type="quarter-circle"
                  direction="down-left"
                  :style="{ position: 'absolute', left: 0, bottom: 0 }"
                  buttonClass="p-button-text p-button-secondary"
                  :showIcon="'pi pi-ellipsis-v'"
                  :hideIcon="'pi pi-times'"
              />
            </div>
          </div>
        </template>
        <template #content>
          <div class="space-y-4">
            <div class="max-w-3xl">
              <span class="font-semibold">Адрес: </span>{{ customer?.address }}
            </div>
            <div>
              <span class="font-semibold">Телефон: </span>{{ customer?.phone || '—' }}
            </div>
            <div>
              <span class="font-semibold">E-mail: </span>
              <a
                  v-if="customer?.email"
                  :href="`mailto:${customer?.email}`"
                  class="text-blue-600 hover:underline"
              >{{ customer?.email }}</a>
            </div>
            <ResponsibilityList
                :list="customerResponsiblesRaw"
                singularTitle="Ответственный"
                pluralTitle="Ответственные"
                :removable="true"
                entityType="customer"
                :entityId="customerId"
                :showPriority="false"
                :allowAdd="true"

            />

            <Contacts v-if="customer?.id" target="customer" :id="customer.id" />
          </div>
        </template>
      </Card>
    </div>

    <CustomerForm
        v-model:visible="showEditDialog"
        :customer="customer"
        @save="handleEditSave"
        @close="showEditDialog = false"
    />
    <ConfirmDeleteDialog
        v-model:visible="showConfirm"
        :customerName="customer?.short_name || customer?.name || ''"
        @confirm="onDeleteConfirm"
    />
    <ConfirmArchiveDialog
        v-model:visible="showConfirmArchive"
        mode="archive"
        :customerName="customer?.name || ''"
        @confirm="onArchiveConfirm"
    />
    <ConfirmArchiveDialog
        v-model:visible="showConfirmUnarchive"
        mode="unarchive"
        :customerName="customer?.name || ''"
        @confirm="onUnarchiveConfirm"
    />
    <CreateReminderDialog
        v-if="reminderTarget"
        v-model="showCreateReminder"
        :targetAppLabel="reminderTarget.app"
        :targetModel="reminderTarget.model"
        :targetObjectId="reminderTarget.objectId"
    />
    <EntityMessageDialog
        v-model="showMessageDialog"
        v-if="customer"
        :user-id="responsible.id"
        :responsible-name="responsible.name || 'Ответственный'"
        entity-type="customer"
        :entity-id="customer?.id"
        :entity-title="customer?.short_name || customer?.name || `#${customer?.id}`"
        :entity-url="`/customers/${customer?.id}`"
    />
    <TaskCreateDialog
        v-if="taskContext"
        v-model="showCreateTask"
        :contextAppLabel="taskContext.app"
        :contextModel="taskContext.model"
        :contextId="taskContext.objectId"
        :defaultAssigneeId="responsible.id"
        :contextTitle="customer?.short_name || customer?.name || `#${customerId}`"
        @created="() => {/* опционально: обновить локальный список задач блока сущности, если он есть */}"
    />
    <EventHistoryDialog
        v-model:visible="showEventHistory"
        target="customer"
        :entityId="customerId"
        :entityName="customer?.name"
    />
  </div>
</template>

<style scoped>

:deep(.p-card-body) {
  padding-top: 0;
  padding-bottom: 8px;
}

</style>